import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  AlertTriangle,
  Zap,
  Droplet,
  Flame,
  Snowflake,
  Sun,
  Wind,
  Skull,
  Activity,
  Scale,
  Sparkles,
  Loader2,
  CheckCircle2
} from 'lucide-react';

import { API_URL } from '../config';

export default function ReferentielsCirconstancielPage() {
  const [aiLoading, setAiLoading] = useState({});
  const [aiResults, setAiResults] = useState({});

  const handleAiAnalysis = async (pathologyId, pathologyName, content) => {
    setAiLoading({ ...aiLoading, [pathologyId]: true });
    
    try {
      const response = await fetch(`${API_URL}/api/ai/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          context: 'referentiels_circonstanciel',
          audience: 'adult',
          prompt: `Analyse clinique pour ${pathologyName}:

${content.slice(0, 500)}

Fournis une analyse synthétique avec:
1. Points clés du diagnostic
2. Éléments de gravité à rechercher
3. Premiers gestes thérapeutiques
4. Pièges à éviter
5. Critères d'orientation`
        })
      });

      if (response.ok) {
        const data = await response.json();
        setAiResults({ ...aiResults, [pathologyId]: data.response });
        toast.success(`Analyse AI ${pathologyName} complétée`);
      } else {
        toast.error('Erreur lors de l\'analyse');
      }
    } catch (error) {
      console.error('AI Analysis error:', error);
      toast.error('Erreur de connexion');
    } finally {
      setAiLoading({ ...aiLoading, [pathologyId]: false });
    }
  };

  const pathologies = [
    {
      id: 'anaphylaxie',
      name: 'Anaphylaxie',
      icon: AlertTriangle,
      color: 'text-red-400',
      bg: 'bg-red-500/10',
      border: 'border-red-400/30',
      content: `**ANAPHYLAXIE**

**DÉFINITION - Critères Sampson = Installation aiguë:**
• Atteinte cutanéo-muqueuse + au moins un signe d'atteinte respiratoire ou d'hypoTA ou de signe de mauvaise perfusion d'organe
• Ou 2 éléments après exposition probable allergène : [Atteinte cutanéo-muqueuse, atteinte respiratoire, hypoTA, signes gastrointestinaux persistants]
• Ou hypoTA après exposition à allergène connu:
  - 1 à 10 ans = PAS < 70 + (2 x âge) mmHg
  - > 11 ans = PAS < 90 mmHg

**POSOLOGIE AÉROSOLS D'ADRÉNALINE:**
• 0,1 mg/kg, max 5 mg

**ADRÉNALINE - ADULTE:**
• **IM:** 1 mg dans seringue de 1 ml soit 0,1 mg par 0,1 ml
  - Dose : 0,01 mg/kg, max 0,5 mg
• **IV bolus:** 1 mg dans seringue 20 ml soit 50 µg (0,05 mg)/ml
  - Bolus titration 50 µg toutes les 2 min
• **IVSE:** 0,05 à 0,1 µg/kg/min adulte

**ADRÉNALINE - ENFANT:**
• **IM:** Pur 1 mg dans seringue de 1 ml soit 0,1 mg par 0,1 ml
  - Dose : 0,01 mg/kg, max 0,5 mg
• **IV bolus:** 0,1 mg dans seringue 10 ml soit 10µg (0,01 mg)/ml
  - 1µg/kg chez enfant toutes 2 min
• **IVSE:** 0,1 µg/kg/min
• **Aérosol:** 0,1 mg/kg max 5 mg (avec SSI qsp 5 ml)

**À SAVOIR:**
• Pas de contre-indication à la voie intramusculaire même chez les patients sous anticoagulants
• Les corticoïdes seuls ne sont pas un traitement de l'anaphylaxie
• Prélèvement tryptase entre 30 min et 2 heures (violet vert ou rouge - hépariné ou EDTA ou sérum)
• Pensez à augmenter les doses d'adrénaline chez les patients sous bêtabloquants (intérêt du glucagon ++)`
    },
    {
      id: 'electrisation',
      name: 'Électrisation',
      icon: Zap,
      color: 'text-yellow-400',
      bg: 'bg-yellow-500/10',
      border: 'border-yellow-400/30',
      content: `**ÉLECTRISATION**

**DIAGNOSTIC - 3 définitions distinctes:**
• **Électrisation:** manifestations physiologiques et physiopathologiques dues au passage du courant électrique (CE) au travers du corps humain
• **Électrotraumatisme:** ensemble des lésions découlant de l'accident électrique (brûlures, trauma direct, projections, etc…)
• **Électrocution:** décès par électrisation

⚠️ Attention aux lésions associées : Brûlure, Blast, Fractures, Intoxication...

**SIGNES DE GRAVITÉ:**
• Perte de connaissance, signes ECG, brûlures et traumatismes, déficit neurologique
• Terrain (Comorbidités, Femme enceinte)

**LA GRAVITÉ DU TABLEAU DÉPEND:**

**De l'intensité du courant: «l'intensité tue»**
• > 10mA: contraction musculaire, risque d'agrippement à la source (muscles fléchisseurs) ou de projection (muscles extenseurs)
• Vers 30mA: risque d'asphyxie par tétanisation et paralysie des muscles respiratoires
• 50-100mA = seuil cardiaque : risque d'ACR par FV

**De la tension du courant: «la tension brûle»**
• < 1000V (accidents domestiques): brûlures tissulaires modérées
• > 1000V (accidents professionnels): brûlures tissulaires profondes et sévères et asystolie d'emblée

**Du temps de contact** (patient agrippé ou non?)
• Courant continu > courant alternatif

**Du trajet, transcardiaque ++**
• Long: verticale (main pied, tête pied)
• Transthoracique (main à main)

**CONDUITE À TENIR:**

**• Éviter le sur-accident:**
- S'assurer que les lieux sont sécurisés et le courant coupé avant d'intervenir
- Si haute tension: uniquement par les pompiers ou EDF

**• Victime en ACR:**
- Prise en charge habituelle de tout ACR (cf. protocole ACR)

**• En dehors de l'ACR:**
- Respect de la rectitude rachidienne
- Oxygénothérapie
- Surveillance hémodynamique et réhydratation avec 2 VVP
- Examen clinique complet, avec recherche en particulier:
  > Brûlures (points d'entrée et de sortie, voire règle de Wallace des 9 si brûlures étendues)
  > Traumatisme osseux
  > Traumatisme des parties molles
  > Traumatisme crânien
- Lutte contre l'hypothermie et contrôle des ACSOS
- IOT si nécessaire

**• ECG rapide et répété** pour dépister:
- Tachycardies sinusales
- Modifications non spécifiques du segment ST et de l'onde T
- Extrasystoles ventriculaires
- Épisodes de tachycardies supra-ventriculaires, de fibrillation auriculaire
- Troubles de conduction
- Bradycardies

⚠️ **ATTENTION:** patient à risque +++ d'hyperkaliémie aiguë

**ORIENTATION:**
• Aucun signe de gravité et ECG normal: transport vers SAU le plus proche
• Examen clinique normal mais ECG perturbé: Transport médicalisé vers le SAU disposant au minimum d'une unité de surveillance continue
• Transport médicalisé vers la réanimation après régulation:
  - ACR récupéré
  - Anomalie de l'examen neurologique d'origine centrale ou lésion médullaire
  - Brûlure sévère (2e, 3e degrés > 10 % de la surface corporelle) envisager centre des grands brûlés
  - Atteinte viscérale ou vasculaire extensive
  - Signes ECG sévères`
    },
    {
      id: 'accident-plongee',
      name: 'Accident de Plongée',
      icon: Droplet,
      color: 'text-blue-400',
      bg: 'bg-blue-500/10',
      border: 'border-blue-400/30',
      content: `**ACCIDENT DE PLONGÉE**

**DIAGNOSTIC POSITIF - Plusieurs tableaux différents:**

**• Barotraumatisme ORL:**
- Douleur +/- otorragie, épistaxis +/- vertige associé

**• Surpression pulmonaire** (remontée rapide sans expiration ou à expiration insuffisante):
- Dyspnée + douleur thoracique +/- hémoptysie
- +/- signes de pneumothorax
- +/- signes neurologiques associés = embolie gazeuse

**• Accident de décompression:**
**Bénin:**
- Cutané ("puce" = prurit, "moutons" = éruption en cartes de géographie)
- Ostéo-articulaire = vive douleur, en général de l'épaule ou de la hanche

**Sévère:**
- Signes d'ischémie médullaire ou cérébrale
- Cochléo-vestibulaire (vertige)
- Coronaire (SCA ST+)

**• Vertige alternobarique:**
- Syndrome vestibulaire périphérique déclenché par les changements de profondeur

**• Œdème pulmonaire d'immersion:**
- OAP généralement amélioré dès la sortie de l'eau

**• Intoxications associées:**
- **CO2** = "essoufflement": dyspnée, polypnée, céphalées, troubles de la concentration
- **CO:** céphalées, vomissements, inconscience
- **Hyperoxie:** crise comitiale
- **Narcose à l'azote:** euphorie et troubles du comportement débutant au-delà de 35m et s'améliorant dès que la remontée est initiée

**ÉLÉMENTS DE GRAVITÉ:**
• Tout signe neurologique même minime
• Pneumothorax suffocant
• Suspicion d'intox au CO avec présence de troubles de conscience

**CONDUITE À TENIR:**

**• Prise en charge initiale commune:**
- Déséquiper, sécher et installer à l'abri avec couverture de survie
- En cas d'ACR débuter la réanimation par 5 insufflations (= ACR hypoxique, avec possible noyade) et la prolonger vu l'hypothermie souvent associée
- Conditionnement avec scope, ECG, et position:
  > Décubitus dorsal
  > PLS si inconscience
  > Demi-assis si dyspnée
- O2 15L/min au MHC
- En cas d'IOT:
  > FiO2 à 100%
  > Limiter les volumes courants et les pressions de crêtes
  > Régler une PEP en cas d'œdème pulmonaire
- VVP et réhydratation IV ≥ 1,5l de SSI en 2h (sauf si OAP)
- ASPÉGIC 250mg per os ou IVL, en l'absence d'allergie, de saignement ou de suspicion de barotraumatisme
- Traitement symptomatique (ACÉTYL-LEUCINE (Tanganil©), MÉTOCLOPRAMIDE (Primpéran©), VNI si OAP)
- MÉTHYLPREDNISOLONE 1 à 2mg/kg IVL en cas de signes neuro (para/hémiplégie, douleur lombaire en barre, troubles sphinctériens)
- En cas de pneumothorax suffocant: exsufflation à l'aiguille

**• Surveillance:**
- Récupérer l'ordinateur et les informations sur le profil de plongée
- Placer les autres membres de la palanquée sous surveillance
- En cas de décès, déclarer un obstacle médico-légal

**TRANSPORT:**
La décision se prend en concertation avec le médecin régulateur et le médecin référent du caisson hyperbare au CHU de Brest La Cavale Blanche :
• **Ligne directe:** 02 98 34 70 98
• **Standard (nuit et week-end):** 02 98 22 33 33

**De manière générale:**
- Accident de décompression, surpression pulmonaire, intoxication au CO sévère (souffrance myocardique, troubles de conscience): caisson hyperbare
- Autres pathologies: au SAU du CH le plus proche
- Syndrome vertigineux: discuté au cas par cas

**Transport:**
- Vers le caisson: par le vecteur le plus rapide, sachant qu'en cas de vol en hélico celui-ci doit s'effectuer à basse altitude (< 300m)
- Vers le SAU: transport non médicalisé sauf si présence de signes de gravité (OAP sévère, syndrome vestibulaire sévère)`
    },
    {
      id: 'noyade',
      name: 'Noyade',
      icon: Droplet,
      color: 'text-cyan-400',
      bg: 'bg-cyan-500/10',
      border: 'border-cyan-400/30',
      content: `**NOYADE**

**DÉFINITION:**
Interruption de la ventilation par irruption d'eau (même quelques millilitres) dans les voies aériennes lors d'une immersion (voies aériennes supérieures) ou submersion (corps entier)

**CLASSIFICATION DE SZPILMAN - 6 stades cliniques:**

**Stade 1:** Pas de toux, pas de mousse
**Stade 2:** Toux, mousse dans la bouche ou le nez
**Stade 3:** OAP (râles crépitants audibles, mousse dans la bouche et le nez)
**Stade 4:** OAP + détresse respiratoire (cyanose, FR >20/min)
**Stade 5:** ACR (pouls présent)
**Stade 6:** ACR (pouls absent)

**CONDUITE À TENIR:**

**• Extraire du milieu** en décubitus dorsal à l'horizontale
**• Maintenir axe tête-cou-tronc** et poser un collier cervical rigide

⚠️ **Tout noyé est un traumatisé jusqu'à preuve du contraire**

**• Rassurer, sécher, réchauffer ++**

**• Prise en charge spécifique:**
- PLS avec masque à haute concentration 15L/min
- Surveillance scopée, conscience, efficacité de la toux
- VVP

**Stade 3 et plus:**
• **Maintien de l'hématose:**
  - VNI FiO2 100% PEP 5 à 7 si monodéfaillance respiratoire
  - IOT si atteinte neurologique ou hémodynamique associée
• Pose de sonde nasogastrique en aspiration
• Remplissage selon chiffres tensionnels
• **Pas d'aspiration si non intubé** (risque d'inhalation)

**Stades 5 et 6 = PEC de l'ACR d'origine respiratoire:**
• Débuter réa par **5 insufflations**
• IOT avec FiO2 100% et PEP 0
• En cas de nécessité de CEE: bien sécher la victime et la placer dans un endroit sec au préalable
• Si hypothermie : cf protocole hypothermie

**ORIENTATION:**
• **Stades 1 et 2:** transfert non médicalisé vers CH le plus proche
• **Stade 3:** transfert médicalisé vers CH disposant d'une USC
• **Stades 4 et plus:** transfert médicalisé vers service de réanimation

**À SAVOIR:**
Monodéfaillance respiratoire est une indication d'oxygénothérapie haut débit en réanimation (type Optiflow©)`
    },
    {
      id: 'hypothermie',
      name: 'Hypothermie',
      icon: Snowflake,
      color: 'text-blue-300',
      bg: 'bg-blue-500/10',
      border: 'border-blue-400/30',
      content: `**HYPOTHERMIE**

**DIAGNOSTIC:**
T° < 35°C (penser à utiliser thermomètre hypotherme ; ou sonde rectale sur scope)

**ÉTIOLOGIES:**

**Hypothermies à défenses minimales:**
- Moyens de défense vis-à-vis du froid altérés
- Intoxication médicamenteuse ou éthylique empêchant le patient de se soustraire à l'environnement extérieur
- Malnutrition, misère physiologique
- Hypothyroïdie et altération de la thermogenèse

**Hypothermies à défenses maximales:**
- Accident de montagne
- Immersion en eau froide
- Polytraumatisme

**CLASSIFICATION DE GRAVITÉ:**

**Grade I (32-35°C):**
- Conscience préservée
- Frissons

**Grade II (28-32°C):**
- Somnolence, confusion
- Disparition des frissons

**Grade III (24-28°C):**
- Inconscience
- Bradypnée
- Bradycardie, arythmies

**Grade IV (13,7-24°C):**
- ACR apparent
- Pas de signes vitaux

**Grade V (<13,7°C):**
- Mort apparente
- Rigidité

**CONDUITE À TENIR:**

**• T°c > 32°C:**
Rien n'est nécessaire en pré-hospitalier autre que l'extraction du milieu froid (vêtements secs, couverture de survie)

**• T°c < 32°C:**
• Réchauffement passif par couverture à air pulsé [+ 0,5°C/h]
• **ECG:** onde J

**Activité circulatoire spontanée:**
- Stratégie de «stress minimum»
- Éviter tout geste invasif pouvant conduire à la survenue d'une FV
- Pas de SNG, limiter les aspirations au strict nécessaire
- Pas de VVC en territoire cave supérieur

**ACR = MCE + VA (IOT):**
• **Si asystolie et <30°C:** pas d'adrénaline (inefficace)
• **À partir de 30°C:** adrénaline toutes les 6-10 min
• **Après 35°C:** prise en charge standard

• **Si FV:** essai 3 CEE et attendre >30°C pour réitérer
• Amiodarone et autres médicaments que si >30°C

• **Discuter transfert sous MCE+VA à St Brieuc pour ECMO**

• **Réanimation jusqu'à réchauffement** ("on est mort que lorsqu'on est mort et réchauffé")
• Arrêt de réanimation à discuter au cas par cas

• **Une fois à l'hôpital:** si patient en ACR (massé et ventilé) => un gaz du sang permet de décider rapidement ou non de la poursuite de la réanimation
  - Si K>10 mmol/L ou pH<6,5 = stop

**TRANSPORT:**
• Médicalisé et monitoré (SpO2, FC, TA)
• Envisager orientation ECMO

**À SAVOIR:**
Onde J d'Osborne : spécifique mais non pronostique de l'hypothermie`
    },
    {
      id: 'coup-chaleur',
      name: 'Coup de Chaleur',
      icon: Sun,
      color: 'text-orange-400',
      bg: 'bg-orange-500/10',
      border: 'border-orange-400/30',
      content: `**COUP DE CHALEUR**

**DIAGNOSTIC:**
• **Température supérieure à 40°C**
• **Signes neurologiques ou d'encéphalopathie:** confusion, délire, convulsions, déficit focal, troubles de la conscience, coma
• **Absence d'argument pour une cause infectieuse**

**PRODROMES:**
- Asthénie, douleurs abdo, vomissements, vertiges, crampes, sueurs profuses

**PHASE D'ÉTAT:**
- Peau sèche et parfois rouge

**ÉTIOLOGIES:**
• Essentiellement climatique (sujet âgé en période caniculaire)
• D'exercice (sujet jeune)
• Médicamenteux (syndrome malin des neuroleptiques, syndrome sérotoninergique)
• Toxiques (amphétamines, cocaïne etc.)

**ÉLÉMENTS DE GRAVITÉ:**
• État de choc, défaillance multiviscérale
• Trouble de la conscience, coma
• Défaillance respiratoire avec polypnée, épuisement, hypoxie sévère, SDRA
• Troubles du rythme cardiaque, SCA
• Hémorragie diffuse sur thrombopénie, CIVD
• Insuffisance rénale +/- sévère sur nécrose tubulaire aiguë, hypokaliémie fréquente
• Rhabdomyolyse

**CONDUITE À TENIR:**

**REFROIDISSEMENT ACTIF:**
- Déshabiller, mettre à l'ombre, en courant d'air, humidifier tout le corps
- Poches de glace sur les principaux axes vasculaires
- Vaporisation des téguments avec de l'eau et ventilateur brassant de l'air (au SAU)

**SUPPORT VENTILATOIRE:**
- O2 MHC +/- IOT

**SURVEILLANCE:**
- Monitorage, Température, HGT régulières

**RÉHYDRATATION:**
- Réhydratation per-os si possible
- Sinon 1 à 2 VVP bon calibre: NaCL 0,9% 1 L à 1,5 L la première heure, puis relais sur 24h
- Expansion volémique si choc hypovolémique

**CONVULSIONS:**
- Benzodiazépine Clonazepam (Rivotril®) 1 mg IVD

**TRANSPORT:**
• Médicalisé avec monitoring, véhicule climatisé
• Admission en Réanimation si défaillances

**À SAVOIR:**
• **Paracétamol non efficace** et risque d'aggravation hépatique
• **AINS à éviter** car néphrotoxicité sur possible déshydratation
• **Surveillance HGT** (hypo ou hyperglycémie) surtout chez le diabétique`
    },
    {
      id: 'fumees-incendie',
      name: 'Exposition Fumées d\'Incendie',
      icon: Flame,
      color: 'text-red-400',
      bg: 'bg-red-500/10',
      border: 'border-red-400/30',
      content: `**EXPOSITION AUX FUMÉES D'INCENDIE**

**DIAGNOSTIC:**

**• Multiples principes actifs:**
- Principaux agents létaux : cyanés et monoxyde de carbone (cf protocole CO)

**• Exposition physique aux suies:**
- Toxicité directe des voies aériennes et broncho-pulmonaire

**• Parfois association à:**
- Brûlure (cf protocole brûlure)
- Polytraumatisme (défenestration; cf protocole polytrauma)

⚠️ **L'absence de suies dans l'oropharynx exclut à plus de 90% une intoxication au CO ou aux cyanés**
⚠️ **L'exposition en espace clos aggrave le tableau**

**CLINIQUE:**

**Inhalation de fumées:**
- Suspecter si présence de suies sur le visage, dysphonie, dyspnée, wheezing, et/ou expectorations noirâtres
- Risque d'œdème des voies aériennes, de bronchospasme et d'œdème pulmonaire lésionnel pouvant être retardés

**Intoxication au cyanure:**
Suspecter devant présence de suies de la face et de l'oropharynx associées à:
- Nausées, vomissements, maux de tête, confusion, désorientation
- Mydriase, convulsions ou coma
- Oppression thoracique, dyspnée, tachypnée ou hyperpnée (phase précoce)
- Bradypnée ou apnée (phase tardive)
- Hypertension (phase précoce) ou hypotension (phase tardive)
- Collapsus cardiovasculaire, ACR

**ÉLÉMENTS DE GRAVITÉ:**
• Défaillance hémodynamique, respiratoire, neurologique
• Association à brûlures graves et/ou polytraumatisme (défenestration)

**CONDUITE À TENIR:**

**Traitement non spécifique:**
- Extraction du milieu
- Nettoyage cutané
- Oxygénothérapie 15L MHC, IOT
- VVP
- Traitement symptomatique des défaillances : remplissage, IOT-ventilation,…

**INDICATION ANTIDOTE POUR INTOXICATION AU CYANURE:**

**Adulte:**
- Arrêt respiratoire, coma, état de choc, ACR
- Lactates >8 mmol/l

**Enfant:**
**Intoxication modérée:**
- GCS≤13, confusion, stridor, voix rauque, polypnée, dyspnée avec suies dans les voies aériennes

**Intoxication sévère:**
- GCS ≤ 8, convulsions, coma, mydriase
- Troubles hémodynamiques graves, collapsus
- Dépression respiratoire

**POSOLOGIE HYDROXOCOBALAMINE (Cyanokit©):**

**Adulte:**
- 5g dans 200 ml de NaCl 0,9% sur 30 min, renouvelable une fois
- 10 g si ACR
- Pas de contre-indication chez la femme enceinte

**Enfant:**
- Adolescents et nourrissons, dès la forme modérée
- 70 mg/kg sans dépasser 5 g sur 30 minutes, renouvelable une fois

**SIGNES D'EFFICACITÉ DE L'ANTIDOTE:**
- Reprise d'une activité respiratoire
- Conscience
- Normalisation hémodynamique

**ORIENTATION:**
Selon les cas, après régulation, vers réanimation, caisson hyperbare, centre de grands brûlés ou SAU`
    },
    {
      id: 'intox-co',
      name: 'Intoxication au CO',
      icon: Skull,
      color: 'text-purple-400',
      bg: 'bg-purple-500/10',
      border: 'border-purple-400/30',
      content: `**INTOXICATION AU CO**

**DIAGNOSTIC:**

**Signes cliniques:**
- Contexte de symptômes collectifs
- **Signes neuro-psychiatriques:** céphalées, vertiges, ébriété, agitation
- Malaise, troubles de conscience
- Asthénie, faiblesse musculaire, troubles visuels
- Douleurs abdominales et troubles digestifs
- Douleur thoracique

**En cas d'incendie - suspectée par syndrome d'inhalation:**
- Espace clos et signes neurologiques
- Suie endocavitaire
- Brûlure faciale

**Signes paracliniques:**
- Dosage dans l'air > 100ppm par détecteurs mobiles (pompiers ++)
- **RAD 57** (estimation transcutanée type saturomètre):
  > Non-fumeur: HbCO > 5%
  > Fumeur: HbCO > 10%
  
**Technique RAD 57:**
- Doigt propre
- Capteur sous un linge pour masquer la lumière
- Moyenne des mesures faite sur 3 doigts différents des deux mains
- Tenir compte d'une marge d'erreur de 3 points

**En cas de décès:**
- Teinte cochenille des téguments
- Présence de plusieurs victimes
- Atteinte des animaux domestiques

**ÉLÉMENTS DE GRAVITÉ = INDICATION À OXYGÉNOTHÉRAPIE HYPERBARE:**
• Atteinte cardiovasculaire : SCA, OAP, collapsus
• Atteinte neurologique : malaise, convulsion, coma hypertonique
• HbCO > 25%
• Femme enceinte

**CONDUITE À TENIR:**

- **Bien noter les horaires:** extraction de l'atmosphère, mise sous oxygène, prélèvement…
- Rechercher diagnostic différentiel et/ou une pathologie associée (ex. : IMV, traumatisme crânien grave, intoxication aux fumées d'incendies, etc…)

- **Repos strict et surveillance scopée**
- ⚠️ **Ne pas se fier à la SpO2 faussement normale**

- **O2 12 à 15L au MHC**
- En cas de coma, IOT et VAC en maintenant la FiO2 à 100%

- **Effectuer la gazométrie artérielle le plus rapidement possible** (faux négatifs après O2)
- **ECG** selon la symptomatologie (douleur thoracique, dyspnée, coma)
- **Traitement symptomatique** des complications associées

- Si contexte d'incendie : cf protocole exposition fumées

- **Discussion caisson hyperbare** si signes de gravité avec le médecin référent du caisson hyperbare à contacter sans délai au CHRU Brest de La Cavale Blanche:
  > **Ligne directe:** 02 98 34 70 98
  > **Standard (nuit et week-end):** 02 98 22 33 33

**TRANSPORT:**
• **En cas de signes de gravité:** transport médicalisé au caisson hyperbare pour oxygénothérapie hyperbare
• **Dans les autres cas:** transport non médicalisé vers le SAU le plus proche pour 12h d'oxygénothérapie normobare

⚠️ **DÉCLARATION OBLIGATOIRE** (via le centre antipoison d'Angers)`
    },
    {
      id: 'envenimation-viperine',
      name: 'Envenimation Vipérine',
      icon: Activity,
      color: 'text-green-400',
      bg: 'bg-green-500/10',
      border: 'border-green-400/30',
      content: `**CONDUITE À TENIR DEVANT UNE ENVENIMATION VIPÉRINE**

**DIAGNOSTIC:**
Veninémie : pic maximal 30 minutes. Demi-vie d'élimination: 8 heures

**MESURES GÉNÉRALES:**
- VVP, scope
- Repos ; ablation des bagues
- Désinfection locale
- **Pas de garrots**
- Vérification du vaccin anti tétanique
- Pas d'indications aux corticoïdes ni aux traitements topiques

**CLASSIFICATION ET CONDUITE À TENIR:**

**GRADE 0:**
**Signes:** Trace de 2 crochets espacés de 5 à 10 mm au niveau de la morsure (le plus souvent à une extrémité), pas d'œdème ou de réaction locale

**CAT:** Surveillance 6 heures (vérifier l'absence de passage à grade plus élevé)

**GRADE 1:**
**Signes:** Œdème localisé (en général à la main ou le pied ne dépassant pas les styloïdes ou malléoles), absence de signes généraux

**CAT:**
- Surveillance 24 heures
- Traitement antalgique (pas d'AINS)
- **Bilan:** NFS, TP, INR, fibrinogène, PDF, créatinine, CPK toutes les 12 heures
- Passage au grade 2 peut se faire de 8 à 24 heures après le début de l'œdème

**GRADE 2:**
**Signes - 2 tableaux différents peuvent apparaître:**

**Tableau 1:**
- Apparition de signes généraux modérés: hypotension + bradycardie (ou absence de tachycardie)
- Signes anaphylactoïdes (dyspnée laryngée, œdème pharyngolaryngée et érythème cutané diffusé)

**Tableau 2:**
- Œdème expansif du membre (en 8 à 24 heures)
- +/- adénopathies satellites douloureuses, hématome local
- Suffusions pétéchiales sur les trajets veineux
- Parfois signes digestifs (douleur abdo, vomissements, diarrhées)

**Signes biologiques (20 % des cas):**
- GB ≥ 15 000
- Plaquettes ≤ 150 000
- TP ≤ 60%
- Fg ≤ 1,5 g/l
- Pbf +

**CAT:**
- Remplissage par cristalloïdes, support catécholamine si besoin
- **Pas d'antibiothérapie en systémique**
- Si infection ou nécrose, association amoxicilline-ac. clavulanique préconisée (2% d'infection si nécrose au niveau de la morsure)
- **Sérum antivenimeux : VIPERFAV**
  > Posologie identique adulte et enfants
  > 1 amp de 4 ml diluée dans 100 ml de Sérum Phy à passer sur 1 heure
  > **Une seule injection**

**GRADE 3:**
**Signes:** Rare, souvent un grade 2 non traité par Ig antivenimeuses
- Œdème extensif au tronc et/ou symptômes généraux sévères (hypotension prolongée, choc ou collapsus résistant au remplissage)
- Signes digestifs positifs (diarrhée : signe de gravité)
- Biologie positive le plus souvent

**Complications:**
- OAP lésionnel hémorragique
- Insuffisance rénale aiguë organique
- Hémorragies digestives

**CAT:**
- **Sérum antivenimeux : VIPERFAV**
  > Posologie identique adulte et enfants
  > 1 amp de 4 ml diluée dans 100 ml de Sérum Phy à passer sur 1 heure
  > **Une seule injection**

**ORIENTATION:**
• **Grade 0 et 1:** sur urgences de secteur
• **Grade 2 et 3:** discussion avec régulation (réanimation directe ou arrêt pour antidote le plus proche)`
    },
    {
      id: 'obesite-urgences',
      name: 'Obésité et Urgences',
      icon: Scale,
      color: 'text-pink-400',
      bg: 'bg-pink-500/10',
      border: 'border-pink-400/30',
      content: `**OBÉSITÉ ET URGENCES: PARTICULARITÉS**

**«POIDS» À CONSIDÉRER CHEZ L'OBÈSE:**

• **Poids réel:** PR
• **Poids idéal théorique:** PIT

**Formule PIT:**
PIT = X + 0,91 x (taille cm - 152,4)
- X = 50 (homme)
- X = 45,5 (femme)

**EXAMEN CLINIQUE = NON FIABLE**
Seules 3 valeurs cliniques sont fiables:
- Tachycardie
- Détresse respiratoire
- Trouble de la vigilance (examen neurologique)

**ACCÈS VEINEUX:**
- VVP souvent difficile, favoriser rapidement les voies centrales si VVP impossible +/- sous doppler
- Voire voie intra-osseuse
- **Voie fémorale** = voie d'urgence mais risque accru d'infection (intertrigo, macération) chez obèse
- Si opérateur entraîné, préférer la voie jugulaire interne

**MONITORING:**

**Oxymètre de pouls:**
- Préférer le capteur au niveau de la zone temporale, du nez, voire du plus petit des doigts ou des orteils

**Pression artérielle:**
- Brassard, toujours risque de surestimation de 20 à 30% des PA
- Taille nécessaire : largeur x 1,5 circonférence du bras
- Si choc : monitoring par cathéter artériel huméral ou fémoral

**ECG altéré:**
- Microvoltage, aplatissement ondes T voire inversion, déviation axiale G
- Diagnostic d'hypertrophie ventriculaire G ou d'ischémie sur critères strictement électriques est incertain

**VENTILATION:**

**Risque de ventilation manuelle difficile si:**
- IMC > 30 kg/m²
- Age > 55 ans
- SAS
- Ronflement
- Barbe
- Protraction mandibulaire limitée

⚠️ **Considérer comme à risque d'IOT difficile dans tous les cas**

**Apnée pré-intubation réduite chez les obèses:**
- Risque hypoxique lors de l'induction

**EN PRÉ-INTUBATION:**
- Masque facial adapté et canule de Guédel prête
- Préparer le matériel d'intubation difficile dont mandrin d'Eschmann et lames droites adaptées
- Position proclive à 45° et/ou amendée de Jackson
- **Pré-oxygénation prolongée indispensable** (idéalement, au moins 5 min):
  > Favoriser la VNI en mode VS-AI +PEP

**LORS DE LA VENTILATION MÉCANIQUE:**
- VT 6 à 8 mL/kg Poids idéal théorique en fonction de l'indication de ventilation
- Intérêt d'une PEP élevée, à titrer (max 10 cmH2O)
- Surveillance obligatoire de la capnographie

**MÉDICAMENTS ET OBÉSITÉ:**

**GÉNÉRALITÉS:**
• **Biodisponibilité** = non modifiée
  - La voie orale peut être utilisée sans restriction
  - Éviter la voie SC, imprévisible

• **↗ Masse grasse/masse maigre** = ↗ Volume de distribution (Vd) des produits lipophiles
  - Substance lipophiles : calcul du Poids Réel PR
  - Substance hydrophiles : calcul du Poids Idéal Théorique PIT

• **Élimination hépatique et rénale:**
  - Augmentation du DFG mais risque d'insuffisance rénale fréquente (diabète type II, HTA etc.)
  - Formule de Cockroft non valide chez obèse
  - MDRD possible
  - Pas de modification du métabolisme hépatique (hormis si co-morbidités)

**MÉDICAMENTS : PIT ou PR À CONSIDÉRER:**

**Hypnotiques:**
- **Propofol:** induction + entretien sur PR
- **Benzodiazépines:** PR pour dose de charge puis PIT pour les doses d'entretien

**Morphiniques:**
- **Fentanyl:** PR
- **Sufentanil:** PR pour dose de charge puis PIT pour les doses d'entretien

**Curares:**
Chez IMC > 40, préférer curare à délai d'action courte : préférer en premier Succinylcholine à 1mg/kg sur PR
- **Succinylcholine (Célocurine):** PR
- **Atracurium et Cisatracurium:** PIT car pharmaco identique au non obèse
- **Rocuronium (Esmeron):** PIT
- **Sugammadex:** PIT chez patient IMC > 40

**Antibiotiques:**
- Tous en PIT
- **Sauf:** vancomycine et amphotéricine : PR
- Intérêt des dosages chez obèse en choc septique (variabilité du volume de distribution)

**Anticoagulation:**
Facteur de risque de thrombose si IMC > 30
- **HBPM préventives** X2/j (maxi 10 000 UI/j)
- **HBPM curatives:** doses définies selon le PR → dosage anti-Xa
- **Prophylaxie du patient obèse en choc:** HNF IVSE + dosage anti-Xa`
    }
  ];

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 bg-gradient-to-br from-orange-500/20 to-yellow-500/20 rounded-xl border border-orange-400/30">
            <AlertTriangle className="w-8 h-8 text-orange-400" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-[hsl(var(--text))]">Référentiels Circonstanciel</h1>
            <p className="text-[hsl(var(--text-dim))]">Urgences liées aux circonstances et environnement</p>
          </div>
        </div>
        
        <Alert className="bg-orange-500/10 border-orange-400/30">
          <AlertTriangle className="w-4 h-4 text-orange-400" />
          <AlertDescription className="text-[hsl(var(--text))]">
            <strong>Protocoles SMUR 2025 :</strong> Référentiels pour les urgences circonstancielles et environnementales
          </AlertDescription>
        </Alert>
      </div>

      {/* Main Content */}
      <Card className="bg-[hsl(var(--bg-elev))] border-[hsl(var(--border))]">
        <CardHeader>
          <CardTitle className="text-xl flex items-center gap-2 text-[hsl(var(--text))]">
            <Activity className="w-5 h-5 text-orange-400" />
            Pathologies Circonstancielles
          </CardTitle>
          <CardDescription>
            Sélectionnez une pathologie pour afficher le protocole détaillé
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[600px] pr-4">
            <Accordion type="single" collapsible className="w-full space-y-4">
              {pathologies.map((path) => {
                const Icon = path.icon;
                const isLoading = aiLoading[path.id];
                const aiResult = aiResults[path.id];

                return (
                  <AccordionItem
                    key={path.id}
                    value={path.id}
                    className="border border-[hsl(var(--border))] rounded-lg overflow-hidden bg-[hsl(var(--bg))]"
                  >
                    <AccordionTrigger
                      className="px-6 py-4 hover:no-underline hover:bg-[hsl(var(--bg-elev))] transition-colors"
                      data-testid={`accordion-trigger-${path.id}`}
                    >
                      <div className="flex items-center gap-3 w-full">
                        <div className={`p-2 ${path.bg} rounded-lg border ${path.border}`}>
                          <Icon className={`w-5 h-5 ${path.color}`} />
                        </div>
                        <span className="font-semibold text-[hsl(var(--text))] text-left">
                          {path.name}
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-6 py-4 space-y-4">
                      <div className="space-y-4">
                        {/* Contenu médical */}
                        <div className="prose prose-invert max-w-none">
                          <div className="text-sm text-[hsl(var(--text))] whitespace-pre-wrap leading-relaxed">
                            {path.content.split('\n').map((line, idx) => {
                              if (line.startsWith('**') && line.endsWith('**')) {
                                return (
                                  <h3 key={idx} className="text-lg font-semibold text-cyan-400 mt-4 mb-2">
                                    {line.replace(/\*\*/g, '')}
                                  </h3>
                                );
                              }
                              if (line.startsWith('• ') || line.startsWith('- ')) {
                                return (
                                  <div key={idx} className="flex items-start gap-2 my-1">
                                    <CheckCircle2 className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                                    <span>{line.substring(2)}</span>
                                  </div>
                                );
                              }
                              if (line.startsWith('⚠️')) {
                                return (
                                  <div key={idx} className="flex items-start gap-2 p-3 bg-orange-500/10 rounded-lg border border-orange-400/30 my-2">
                                    <AlertTriangle className="w-4 h-4 text-orange-400 mt-0.5 flex-shrink-0" />
                                    <span>{line.substring(2)}</span>
                                  </div>
                                );
                              }
                              return <p key={idx} className="my-1">{line}</p>;
                            })}
                          </div>
                        </div>

                        {/* Bouton Analyse AI */}
                        <Button
                          onClick={() => handleAiAnalysis(path.id, path.name, path.content)}
                          disabled={isLoading}
                          className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600"
                          data-testid={`ai-button-${path.id}`}
                        >
                          {isLoading ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Analyse AI en cours...
                            </>
                          ) : (
                            <>
                              <Sparkles className="w-4 h-4 mr-2" />
                              Analyse AI de ce protocole
                            </>
                          )}
                        </Button>

                        {/* Résultat AI */}
                        {aiResult && (
                          <div className="p-6 bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-400/30 rounded-lg">
                            <h3 className="text-lg font-semibold text-cyan-400 mb-4 flex items-center gap-2">
                              <Sparkles className="w-5 h-5" />
                              Analyse AI
                            </h3>
                            <div className="prose prose-invert max-w-none">
                              <div className="text-[hsl(var(--text))] whitespace-pre-wrap text-sm" data-testid={`ai-result-${path.id}`}>
                                {aiResult}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
}
